import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { toastr } from "react-redux-toastr";
import { useParams } from "react-router-dom";

import { ModalTypes } from "components/modals/interfaces";
import { LayerAlias, UnitTypes } from "constant";
import { useLabelingContext } from "context/LabelingContextProvider";
import features from "features";

const DEFAULT_INITIAL_VALUES = {
  // object_diameter: "3",
  name: ""
};

const usePointHandler = () => {
  const { clearPolygonPoints } = useLabelingContext();
  const dispatch = useDispatch();
  const { id } = useParams();

  const parseFields = useCallback((data) => {
    const { name, object_diameter, coordinates, classification } = data;
    const geo_data = {
      coordinates: Object.values(coordinates),
      type: UnitTypes.POINT
    };

    const additional = {
      object_diameter: object_diameter
        ? parseFloat(object_diameter)
        : undefined,
      classification
    };

    return {
      name,
      additional,
      geo_data
    };
  }, []);

  const handleAddPoint = useCallback(
    (updatedValues, onCreatePointSuccess) => {
      const fields = parseFields(updatedValues);

      dispatch(
        features.unitsManager.actions.createUnitRequest({
          params: {
            projectId: id,
            type: UnitTypes.POINT,
            alias: LayerAlias.MANUAL
          },
          fields,
          onSuccess: (res) => {
            dispatch(features.modal.actions.hideModal());
            onCreatePointSuccess(res);
            toastr.success(`Done`, `Point created`);
            clearPolygonPoints();
          }
        })
      );
    },
    [dispatch, parseFields, id]
  );

  const createPoint = useCallback(
    ({ onCreatePointSuccess, coordinates }) => {
      dispatch(
        features.unitsManager.actions.fetchUnitsClassificationsRequest({
          onSuccess: (res) => {
            dispatch(
              features.modal.actions.showModal({
                modalType: ModalTypes.CLASSIFICATION_POINT_MODAL,
                modalProps: {
                  title: "Add point",
                  description:
                    "Add a description or mark the coordinates for a more precise location.",
                  pointsLayerAlias: LayerAlias.MANUAL,
                  currentClassificationData: {},
                  generalPointData: {
                    ...DEFAULT_INITIAL_VALUES,
                    coordinates
                  },
                  classificationsData: res.data[0],
                  params: res.params,
                  onSubmit: (values) =>
                    handleAddPoint(values, onCreatePointSuccess)
                }
              })
            );
          }
        })
      );
    },
    [dispatch, handleAddPoint]
  );

  const addLabeling = useCallback(
    ({ base64Image, unitId, bbox }) => {
      const base64Clean = base64Image.replace(/^data:image\/png;base64,/, "");
      dispatch(
        features.unitsManager.actions.uploadImageRequest({
          params: {
            projectId: id,
            type: UnitTypes.POINT,
            alias: LayerAlias.MANUAL,
            unitId
          },
          fields: {
            content: base64Clean,
            name: "image.png",
            additional: {
              bbox
            }
          },
          onSuccess: () => {
            dispatch(features.modal.actions.hideModal());
            toastr.success(`Done`, `Labeling added`);
          }
        })
      );
    },
    [dispatch, id]
  );

  return { createPoint, addLabeling };
};

export default usePointHandler;
